import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import styles from '@components/ProductCardNew/ProductCardDescription/PriceArea/PriceArea.styles';
import IsShopMiddleware from '@utils/IsShopMiddleware';
import { IGpsrObject } from '@features/categories/typescript';
import Button from '@components/Button/Button';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
type PropsType = {
  price: string;
  priceUnit: string | undefined;
  promoPrice: string;
  omnibusPrice: string;
  showFrom: boolean;
  gpsr?: IGpsrObject | undefined;
  productUrl?: string;
  onLinkClick?: () => void;
  onAuxClick?: (e: React.MouseEvent) => void;
};
const PriceArea = ({
  price,
  priceUnit,
  promoPrice,
  omnibusPrice,
  showFrom,
  gpsr = undefined,
  productUrl,
  onLinkClick,
  onAuxClick
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const renderPriceUnit = (): JSX.Element | null => (price || promoPrice) && priceUnit && priceUnit === 'M2' ? <>
        /m<sup>2</sup>
      </> : null;
  const getPriceAriaLabel = () => {
    if (promoPrice) {
      const fromText = showFrom ? `${t('product.productCostFrom')} ` : '';
      return `${fromText}${t('promotionalPrice')}: ${promoPrice}${priceUnit === 'M2' ? ' /m²' : ''}, ${t('originalPrice')}: ${price}${priceUnit === 'M2' ? ' /m²' : ''}`;
    }
    const fromText = showFrom ? `${t('product.productCostFrom')} ` : '';
    return `${fromText}${t('price')}: ${price}${priceUnit === 'M2' ? ' /m²' : ''}`;
  };
  const handleGpsrClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(gpsr?.informationLabelUrl as string, '_blank');
  };
  const renderTechnicalCard = (): JSX.Element | null => {
    if (!gpsr?.informationSheetUrl) {
      return null;
    }
    return <a href={gpsr?.informationSheetUrl} target="_blank" rel="noreferrer">
        {t('technicalCard')}
      </a>;
  };
  const renderGpsr = (): JSX.Element | null => {
    if (!gpsr?.energyClassCode || !gpsr.energyClassIcon || !gpsr.informationLabelUrl) {
      return null;
    }
    return <div sx={styles.gpsr}>
        <Button variant="iconOnly" variantColor="brand" onClick={handleGpsrClick} size="small">
          <ResponsiveImage image={gpsr?.energyClassIcon} alt={gpsr?.energyClassCode} width={43} height={24} />
        </Button>
      </div>;
  };
  const renderPriceContent = () => <>
      {showFrom && <>
          <span>{t('product.productCostFrom')}</span> {` `}
        </>}
      {promoPrice ? <>
          <span aria-hidden="true">
            {' '}
            {promoPrice}
            {renderPriceUnit()}
          </span>
          <small aria-hidden="true">
            {price}
            {renderPriceUnit()}
          </small>
        </> : <>
          <span aria-hidden="true">
            {' '}
            {price}
            {renderPriceUnit()}
          </span>
        </>}
    </>;
  return <IsShopMiddleware>
      <div sx={styles.priceArea} role="group" aria-label={t('productPricing')}>
        <div sx={styles.priceRow}>
          {productUrl ? <Link href={productUrl} onClick={onLinkClick} onAuxClick={onAuxClick} sx={styles.priceLink(!!promoPrice)} aria-label={getPriceAriaLabel()} tabIndex={0}>
              {renderPriceContent()}
            </Link> : <span sx={styles.productCardInfoPrice(!!promoPrice)} aria-label={getPriceAriaLabel()}>
              {renderPriceContent()}
            </span>}
          <div sx={styles.omnibus}>
            {omnibusPrice && promoPrice && <p className="omnibus" role="status" aria-label={`${t('omnibusText')} ${omnibusPrice}${priceUnit === 'M2' ? ' /m²' : ''}`}>
                {`${t('omnibusText')} ${omnibusPrice}`}
                {renderPriceUnit()}
              </p>}
          </div>
          <div sx={styles.technicalCard}>
            {renderTechnicalCard()} {renderGpsr()}
          </div>
        </div>
      </div>
    </IsShopMiddleware>;
};
export default PriceArea;